/**
 * People Tickets API — /api/people-tickets on the People Space proxy. Every call goes through
 * authenticatedFetch, and every call checks `res.ok` before parsing: a failure that never reached
 * our route (an undeployed endpoint, a load-balancer 502) has no JSON body.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type {
  NewTicket,
  PeopleTicket,
  PeopleTicketOptions,
  PeopleTicketsAccess,
  TicketPriority,
  TicketQueue,
  TicketStatus,
} from '../types/peopleTickets';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL || 'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/people-tickets`;

/** A failed request, carrying the per-field messages a 400 sends back. */
export class TicketRequestError extends Error {
  readonly status: number;
  readonly details: Record<string, string>;

  constructor(message: string, status: number, details: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authenticatedFetch(`${BASE}${path}`, init);
  const data = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    throw new TicketRequestError(
      (data as { error?: string }).error || 'Could not reach the server — please try again.',
      res.status,
      ((data as { details?: Record<string, string> }).details as Record<string, string>) || {},
    );
  }
  return data as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export function fetchPeopleTicketsAccess(): Promise<PeopleTicketsAccess> {
  return request<PeopleTicketsAccess>('/access');
}

export function fetchPeopleTicketOptions(): Promise<PeopleTicketOptions> {
  return request<PeopleTicketOptions>('/options');
}

export async function fetchPeopleTickets(): Promise<PeopleTicket[]> {
  return (await request<{ tickets: PeopleTicket[] }>('')).tickets ?? [];
}

export function submitPeopleTicket(ticket: NewTicket): Promise<{ ticket: PeopleTicket; attachmentsFailed: number }> {
  return request('', json('POST', ticket));
}

type TicketChange =
  | { status: TicketStatus }
  | { priority: TicketPriority }
  | { assigneeEmail: string }
  | { category: string; queue: TicketQueue }
  | { resolutionNote: string }
  | { internalNotes: string };

export async function updatePeopleTicket(id: string, change: TicketChange): Promise<PeopleTicket> {
  return (await request<{ ticket: PeopleTicket }>(`/${encodeURIComponent(id)}`, json('PATCH', change))).ticket;
}

export function fetchPeopleTicketSlackContact(id: string): Promise<{ slackUserId: string }> {
  return request(`/${encodeURIComponent(id)}/slack-contact`);
}

/** A File as the proxy's upload expects it: base64 without the data-URL prefix. */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = `${reader.result ?? ''}`;
      const comma = result.indexOf(',');
      if (comma === -1) reject(new Error(`Could not read ${file.name}.`));
      else resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}
